import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import andUsLogo from "../../images/logolight.png";
import AiWalletStatusBar from "./AiWalletStatusBar";
import TenantSwitcher from "./TenantSwitcher";
import { formatAccessRole } from "../constants/productCatalogue";
import { getTenantBrandingStyle, normalizeTenantBranding } from "../utils/tenantBranding";

const NAV_ICONS = {
  systems: <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />,
  overview: <path d="M4 13h6v7H4zM14 4h6v16h-6zM4 4h6v5H4z" />,
  companies: <path d="M4 20V6l8-3 8 3v14M8 9h1M8 13h1M15 9h1M15 13h1M10 20v-3h4v3" />,
  credits: <path d="M12 3c4.4 0 8 1.6 8 3.5S16.4 10 12 10 4 8.4 4 6.5 7.6 3 12 3Zm-8 3.5V11c0 1.9 3.6 3.5 8 3.5s8-1.6 8-3.5V6.5M4 11v4.5C4 17.4 7.6 19 12 19s8-1.6 8-3.5V11" />,
  users: <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20c.7-4 3.2-6 7.5-6s6.8 2 7.5 6" />,
  admins: <path d="M8.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM3 20c.5-4 2.3-6 5.5-6M16 10v8M12 14h8" />,
  audit: <path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5" />,
  account: <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20c.7-4 3.2-6 7.5-6s6.8 2 7.5 6" />,
  support: <path d="M4 5h16v11H8l-4 4zM8 9h8M8 12h5" />,
};

export function getInitials(name, email) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return String(email || "AU").slice(0, 2).toUpperCase();
}

export default function CentralDashboardShell({
  user,
  organisation,
  navItems,
  activeSection,
  onSectionChange,
  onSignOut,
  navigationDisabled = false,
  signOutPending = false,
  sidebarNote = "Your organisation controls system and role allocation.",
  branding,
  showAiWallet = false,
  canAddAiWalletCredits = false,
  aiWalletTenantId = "",
  accessRole,
  appearance = "default",
  className = "",
  children,
}) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [switchPending, setSwitchPending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const menuToggleRef = useRef(null);
  const sidebarAccount = ["facilitator", "learner", "platform-admin", "tenant-admin"].includes(appearance);
  const hasSidebarDrawer = sidebarAccount;
  const name = user?.displayName || user?.fullName || user?.email || "AndUs user";
  const tenantBranding = normalizeTenantBranding(branding, organisation);
  const navigationLocked = navigationDisabled || signOutPending || switchPending;
  const preventLockedNavigation = (event) => {
    if (navigationLocked) {
      event.preventDefault();
      return;
    }
    setSidebarOpen(false);
  };

  useEffect(() => {
    if (sidebarOpen) sidebarRef.current?.querySelector("nav button")?.focus();
  }, [sidebarOpen]);

  const closeSidebar = () => {
    setSidebarOpen(false);
    menuToggleRef.current?.focus();
  };

  const accountMenu = (
    <div className={`pa-profile-menu${sidebarAccount ? " pa-profile-menu--sidebar" : ""}`}>
      <button type="button" disabled={navigationLocked} onClick={() => setProfileOpen((open) => !open)} aria-label={`Account options for ${name}`} aria-expanded={profileOpen} aria-controls="pa-profile-popover" aria-haspopup="true">
        <span className="pa-avatar">{getInitials(name, user?.email)}</span>
        <span className="pa-profile-menu__copy"><strong>{name}</strong><small>{organisation || "AndUs workspace"}</small></span>
        <span className="pa-profile-chevron" aria-hidden="true">⌄</span>
      </button>
      {profileOpen ? (
        <div className="pa-profile-popover" id="pa-profile-popover">
          <strong>{user?.email}</strong>
          {accessRole ? (
            <div className="pa-profile-popover__role">
              <small>Access role</small>
              <span>{formatAccessRole(accessRole)}</span>
            </div>
          ) : null}
          <TenantSwitcher
            disabled={navigationDisabled || signOutPending}
            onBusyChange={setSwitchPending}
            onSwitched={() => {
              setProfileOpen(false);
              setSidebarOpen(false);
            }}
          />
          <button className="pa-profile-popover__sign-out" type="button" disabled={navigationLocked} onClick={onSignOut}>{signOutPending ? "Signing out…" : "Sign out"}</button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className={`pa-dashboard-shell${appearance === "facilitator" ? " pa-dashboard-shell--facilitator" : appearance === "learner" ? " pa-dashboard-shell--learner" : appearance === "platform-admin" ? " pa-dashboard-shell--platform-admin" : appearance === "tenant-admin" ? " pa-dashboard-shell--tenant-admin" : ""}${className ? ` ${className}` : ""}`}
      data-testid="central-dashboard-shell"
      style={getTenantBrandingStyle(tenantBranding, organisation)}
      onKeyDown={(event) => {
        if (hasSidebarDrawer && sidebarOpen && event.key === "Escape") {
          event.preventDefault();
          setProfileOpen(false);
          closeSidebar();
        }
      }}
    >
      {hasSidebarDrawer && sidebarOpen ? <button className="pa-dashboard-scrim" type="button" aria-label="Close workspace navigation" onClick={closeSidebar} /> : null}
      <aside className="pa-dashboard-sidebar" id="pa-dashboard-sidebar" aria-label="Workspace sidebar" data-open={sidebarOpen} ref={sidebarRef}>
        <Link className="pa-dashboard-logo" to="/" aria-label="AndUs home" aria-disabled={navigationLocked} tabIndex={navigationLocked ? -1 : undefined} onClick={preventLockedNavigation}><img src={andUsLogo} alt="AndUs" /></Link>
        <nav aria-label="Dashboard navigation">
          {navItems.map((item) => {
            const icon = <svg viewBox="0 0 24 24" aria-hidden="true">{NAV_ICONS[item.icon || item.id] || NAV_ICONS.overview}</svg>;
            return (
              <React.Fragment key={item.id}>
                {item.groupLabel ? <span className="pa-dashboard-nav-group">{item.groupLabel}</span> : null}
                {item.path ? (
                  <NavLink to={item.path} end={item.end} aria-disabled={navigationLocked} tabIndex={navigationLocked ? -1 : undefined}
                    className={({ isActive }) => isActive ? "is-active" : ""} onClick={preventLockedNavigation}>
                    {icon}<span>{item.label}</span>{item.count != null ? <small>{item.count}</small> : null}
                  </NavLink>
                ) : (
                  <button className={activeSection === item.id ? "is-active" : ""} type="button" disabled={navigationLocked} onClick={() => {
                    onSectionChange(item.id);
                    setSidebarOpen(false);
                  }}>
                    {icon}<span>{item.label}</span>{item.count != null ? <small>{item.count}</small> : null}
                  </button>
                )}
              </React.Fragment>
            );
          })}
          <Link to="/contactAndUs" aria-disabled={navigationLocked} tabIndex={navigationLocked ? -1 : undefined} onClick={preventLockedNavigation}><svg viewBox="0 0 24 24" aria-hidden="true">{NAV_ICONS.support}</svg><span>Access support</span></Link>
        </nav>
        <div className="pa-dashboard-sidebar__note">
          {tenantBranding.logoUrl ? (
            <img
              className="pa-tenant-brand-mark"
              src={tenantBranding.logoUrl}
              alt={`${tenantBranding.displayName || organisation} logo`}
            />
          ) : null}
          <span>{tenantBranding.logoUrl ? tenantBranding.displayName : "Central access"}</span>
          <p>{sidebarNote}</p>
        </div>
        {sidebarAccount ? accountMenu : null}
      </aside>

      <div className="pa-dashboard-workspace">
        <header className="pa-dashboard-topbar">
          {hasSidebarDrawer ? (
            <button className="pa-dashboard-menu-toggle" type="button" ref={menuToggleRef} aria-label={`${sidebarOpen ? "Close" : "Open"} workspace navigation`} aria-expanded={sidebarOpen} aria-controls="pa-dashboard-sidebar" onClick={() => setSidebarOpen((open) => !open)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
          ) : null}
          <Link className="pa-dashboard-mobile-logo" to="/" aria-label="AndUs home" aria-disabled={navigationLocked} tabIndex={navigationLocked ? -1 : undefined} onClick={preventLockedNavigation}><img src={andUsLogo} alt="AndUs" /></Link>
          <div className="pa-secure-routing"><span />Secure system routing</div>
          {showAiWallet ? (
            <AiWalletStatusBar
              canAddCredits={canAddAiWalletCredits}
              key={aiWalletTenantId || "active-tenant-wallet"}
              tenantId={aiWalletTenantId}
            />
          ) : null}
          {!sidebarAccount ? accountMenu : null}
        </header>
        <main className="pa-dashboard-content">{children}</main>
      </div>
    </div>
  );
}
